import { useState } from 'react';
import { Search, Users } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatCurrency } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type { FeeAccountRow } from '@/types/fees';
import { feesApi } from '../api/fees.api';
import { FEE_STATUS_LABEL, FEE_STATUS_TONE } from '../constants';

/** How many families the picker offers at once. A list, not a register. */
const SHOWN = 8;

export interface PickStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The chosen student. The page loads their account and opens the counter. */
  onPick: (studentUuid: string) => void;
  /** Whose account is being loaded, so only that row spins. */
  busyStudentUuid: string | null;
}

/**
 * Which family is paying.
 *
 * The counter needs an account before it can take money, and the toolbar's
 * "Collect fee" is pressed by somebody standing at the window with a parent in
 * front of them — so this opens on the largest balances and searches by name,
 * admission number or roll number, which is what the parent will say.
 *
 * Settled accounts are not offered. A receipt against a family who owes nothing
 * is the entry an office spends the afternoon reversing, and the register's own
 * rows remain the way to correct one.
 */
export function PickStudentModal({
  isOpen,
  onClose,
  onPick,
  busyStudentUuid,
}: PickStudentModalProps) {
  const [draft, setDraft] = useState('');
  // Searches as the office types, once typing pauses — a parent at the window
  // says a name, and waiting for a Search press made the box look broken.
  const search = useDebouncedValue(draft.trim(), 300);

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      isOpen
        ? feesApi.listAccounts({ page: 1, limit: SHOWN, search, sort: 'balance' })
        : Promise.resolve(null),
    [isOpen, search],
  );

  /** Only families who actually owe something can be paid into. */
  const owing = (data?.items ?? []).filter(
    (account: FeeAccountRow) => account.status !== 'NOT_SET' && Number(account.balance) > 0,
  );

  function close() {
    setDraft('');
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title="Collect fee"
      description="Find the student, then take the payment against their account."
      size="md"
      footer={
        <Button variant="secondary" onClick={close} className="rounded-xl px-5 font-bold text-sm h-10 bg-gray-200 tracking-wide">
          Cancel
        </Button>
      }
    >
      <div role="search">
        <Input
          label="Search for a student"
          labelClassName="text-sm font-bold text-slate-700 dark:text-slate-200"
          placeholder="Name, admission no. or roll no."
          type="search"
          autoComplete="off"
          leftIcon={<Search className="h-4 w-4 text-slate-400" />}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="!rounded-xl !h-10 !text-[13.5px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
        />
      </div>

      {error && (
        <div className="mt-4">
          <QueryError title="Could not load the register" error={error} onRetry={refetch} />
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : owing.length === 0 ? (
        <div className="py-6">
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title={search ? 'Nobody owing matches that' : 'Nobody owes anything'}
            description={
              search
                ? 'Try the admission number, or clear the search to see the largest balances.'
                : 'Every account on this session’s register is settled.'
            }
          />
        </div>
      ) : (
        <div className="mt-4 max-h-[380px] overflow-y-auto custom-scrollbar pr-1 -mr-1">
          <ul className="flex flex-col gap-2">
            {owing.map((account) => (
              <li key={account.studentUuid}>
                <button
                  type="button"
                  disabled={busyStudentUuid !== null}
                  onClick={() => onPick(account.studentUuid)}
                  className={cn(
                    'flex w-full items-center gap-3.5 rounded-2xl p-3 text-left transition-all border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-[#111c2e]',
                    'hover:border-indigo-300 dark:hover:border-indigo-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-950/20 hover:shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                    'disabled:cursor-not-allowed disabled:opacity-55 cursor-pointer',
                  )}
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/80 text-[11px] font-black text-blue-600 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/40"
                    aria-hidden
                  >
                    {getNameInitials(account.fullName)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                      {account.fullName}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-sm text-slate-400">
                      {account.admissionNumber}
                      {account.className !== '—'
                        ? ` · ${account.className} ${account.sectionName}`
                        : ''}
                    </span>
                  </span>

                  <div className="shrink-0 text-right flex flex-col items-end gap-1">
                    <span className=" text-sm font-black tabular-nums text-rose-600 dark:text-rose-400">
                      {formatCurrency(account.balance)}
                    </span>
                    <Badge tone={FEE_STATUS_TONE[account.status]} className="font-bold text-[10.5px]">
                      {FEE_STATUS_LABEL[account.status]}
                    </Badge>
                  </div>

                  {busyStudentUuid === account.studentUuid && <Loader size="sm" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
